import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { stencilSchema } from "../../../lib/schemas/stencilSchema";
import {
  uploadStencil,
  updateStencil,
} from "../../../lib/services/stencilService";
import { useStencils } from "../../../lib/hooks/useStencils";

/* Centered modal with backdrop */
const StencilForm = ({
  stencil,
  setSelectedStencil,
  from,
  onClose,
  setProduct1,
  setProduct2,
}) => {
  const { stencils, setStencils } = useStencils();

  const fromUpdate = from === "Update";

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
  } = useForm({
    resolver: zodResolver(stencilSchema),
    mode: "onChange",
    defaultValues: {
      id: (fromUpdate && stencil?.id) || undefined,
      name: (fromUpdate && stencil?.name) || undefined,
      category_id: (fromUpdate && String(stencil?.category_id)) || undefined,
      subcategory: (fromUpdate && stencil?.subcategory) || undefined,
      pdf: (fromUpdate && stencil?.pdf) || undefined,
      img: (fromUpdate && stencil?.img) || undefined,
      source: (fromUpdate && stencil?.source) || undefined,
    },
  });

  // Close with ESC key
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onSubmit = async () => {
    const id = getValues("id");
    const name = getValues("name");
    const category_id = getValues("category_id");
    const subcategory = getValues("subcategory");
    const source = getValues("source");
    const img = watch("img")[0];
    const pdf = watch("pdf")[0];

    // Update stencil
    if (fromUpdate) {
      const res = await updateStencil({
        ...stencil,
        id,
        name,
        category_id,
        subcategory,
        source,
        img,
        pdf,
      });

      if (res && res.ok) {
        const data = await res.json();
        const newStencils = [
          ...stencils.filter((s) => s.id !== id),
          data.stencil,
        ];
        setSelectedStencil(data.stencil);
        setStencils(newStencils);
        setProduct1(newStencils);
        setProduct2(newStencils);
      } else {
        const error = await res.json();
        alert(`Error updating stencil: ${error.error}`);
      }
    }

    // Create stencil
    else {
      const res = await uploadStencil(
        id,
        name,
        category_id,
        subcategory,
        source,
        img,
        pdf
      );

      if (res && res.ok) {
        const data = await res.json();
        const newStencils = [...stencils, data.stencil];
        setStencils(newStencils);
        setProduct1(newStencils);
        setProduct2(newStencils);
      } else {
        const error = await res.json();
        alert(`Error uploading stencil: ${error.message}`);
      }
    }

    onClose();
  };

  return (
    <div
      className="confirm-overlay rounded mt-5"
      role="presentation"
      onClick={onClose}
    >
      {/* stop propagation so clicks inside the card don't close */}
      <div
        className="confirm-card col-2"
        role="dialog"
        aria-modal="true"
        style={{
          width: "auto",
          backgroundColor: "rgb(237, 71, 9)",
          outline: "none",
          border: "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex flex-row gap-5" style={{ color: "white" }}>
            <div className="d-flex flex-column gap-3" style={{ width: "40vh" }}>
              <div className="form-group flex-row">
                <div>
                  <label className="form-label">Stencil ID</label>
                  <input type="text" {...register("id")} />
                </div>
                <div>{errors.id && <small>{errors.id.message}</small>}</div>
              </div>

              <div className="form-group flex-row">
                <div>
                  <label className="form-label">Stencil Name</label>
                  <input type="text" {...register("name")} />
                </div>
                <div>{errors.name && <small>{errors.name.message}</small>}</div>
              </div>

              <div className="form-group flex-row">
                <div>
                  <label className="form-label">Category ID</label>
                  <input
                    type="number"
                    {...register("category_id", {
                      setValueAs: (val) =>
                        val === "" ? undefined : Number(val),
                    })}
                  />
                </div>
                <div>
                  {errors.category_id && (
                    <small>{errors.category_id.message}</small>
                  )}
                </div>
              </div>

              <div className="form-group flex-row">
                <div>
                  <label className="form-label">Subcategory</label>
                  <input type="text" {...register("subcategory")} />
                </div>
                <div>
                  {errors.subcategory && (
                    <small>{errors.subcategory.message}</small>
                  )}
                </div>
              </div>
            </div>

            <div className="d-flex flex-column gap-3" style={{ width: "40vh" }}>
              <div className="form-group flex-row">
                <div>
                  <label className="form-label">Source</label>
                  <input type="text" {...register("source")} />
                </div>
                <div>
                  {errors.source && <small>{errors.source.message}</small>}
                </div>
              </div>

              <div className="form-group flex-row">
                <div>
                  <label className="form-label">PDF</label>
                  <input type="file" {...register("pdf")} />
                </div>
                <div>{errors.pdf && <small>{errors.pdf.message}</small>}</div>
              </div>

              <div className="form-group flex-row">
                <div>
                  <label className="form-label">Image (JPG only)</label>
                  <input type="file" {...register("img")} />
                </div>
                <div>{errors.img && <small>{errors.img.message}</small>}</div>
              </div>
            </div>
          </div>

          <div className="confirm-actions mt-4">
            <button
              className="btn confirm-no"
              style={{
                outline: "none",
                border: "none",
                color: "white",
                backgroundColor: "darkgreen",
              }}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn confirm-yes"
              style={{
                outline: "none",
                border: "none",
                color: "white",
                backgroundColor: "darkgreen",
              }}
            >
              {from} Stencil
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StencilForm;
